import { Checkbox, Dropdown, Spin } from "antd";
import dayjs from "dayjs";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { MoreOutlined } from "@ant-design/icons";
import CommonIcons from "../../../assets/icons";
import CommonComponents from "../../Commons";
import { LocalStorageKey } from "../../../constants/localStorageKeys";
import { RootState } from "../../../store/store";
import ImageInfo from "../ExploreArchive/ImageInfo";
import Thumbnail from "../ExploreArchive/ThumbnailToken";
import
{
   flyToGeometry,
   getThumbnail,
   removeFootprint,
   removeImageFromMap,
   showFootprint,
   showImageOnMap,
} from "../ExploreArchive/ultis";

const CardItemInCart = ({
   item,
   isSelected,
   isDeleting,
   toggleItem,
   mapInstance,
   onDelete,
   handleViewInfo,
   showCheckBox

}: any) =>
{
   const dispatch = useDispatch();

   const [openInfo, setOpenInfo] = useState(false);
   const [isMapLoading, setIsMapLoading] = useState(false);

   const layersOnMap = useSelector((state: RootState) => state.layer.layers);
   const layerIds = useMemo(
      () => new Set(layersOnMap.map((l: any) => l.id)),
      [layersOnMap],
   );

   const isFootprintShown = layerIds.has(`footerprint-${item?.id}`);
   const isImageShown = layerIds.has(`image-${item?.id}`);

   /* ---------------- STATIC CONFIG ---------------- */
   const staticConfigs = JSON.parse(
      localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
   );

   const baseUrl = staticConfigs?.REACT_APP_API_BASE_URL;

   /* ---------------- ACTIONS ---------------- */

   const withMapLoading = async (fn: () => any) =>
   {
      setIsMapLoading(true);
      try
      {
         await Promise.resolve(fn());
      } finally
      {
         setIsMapLoading(false);
      }
   };

   const menuItems = [
      {
         key: "footprint",
         label: isFootprintShown ? t("Hide footprint") : t("Show footprint"),
         onClick: () =>
         {
            withMapLoading(() =>
            {
               if (isFootprintShown) removeFootprint(item, dispatch);
               else showFootprint(item, dispatch, mapInstance);
            });
         },
      },
      {
         key: "image",
         label: isImageShown ? t("Hide image on map") : t("Show image on map"),
         onClick: () =>
         {
            withMapLoading(() =>
            {
               if (isImageShown) return removeImageFromMap(item, dispatch);
               return showImageOnMap(item, dispatch, item?.provider_code, mapInstance);
            });
         },
      },
      {
         key: "pan",
         label: t("Pan to"),
         onClick: () =>
         {
            flyToGeometry(item?.geometry, mapInstance);
         },
      },
      {
         key: "view", label: t("Information"), onClick: () =>
         {
            if (handleViewInfo) handleViewInfo(item);
            else setOpenInfo(true);
         }
      },
      ...(onDelete
         ? [{ key: "delete", label: t("Remove from cart"), onClick: () => onDelete(item) }]
         : []),
   ];

   /* ---------------- RENDER ---------------- */

   return (
      <>
         <Spin spinning={isMapLoading} size="small">
         <div
            onClick={() =>
            {
               if (showCheckBox)
               {
                  toggleItem(item)
               }
            }}
            className={`item-card-search ${isSelected ? "selected" : ""} ${isDeleting ? "!border-red-500" : ""}`}
         >
            {/* ================== CONTENT ================== */}

            <div className="flex gap-3 sm:gap-4">
               {/* THUMBNAIL */}
               <div className="w-[80px] h-[80px] min-w-[80px] max-w-[80px] shrink-0 overflow-hidden rounded bg-[#0b1323] relative [&_.ant-image]:block [&_.ant-image]:w-full [&_.ant-image]:h-full [&_.ant-image-img]:w-full [&_.ant-image-img]:h-full [&_.ant-image-img]:object-contain">
                  <Thumbnail
                     url={getThumbnail(item?.assets, baseUrl)}
                     token={localStorage.getItem("token") ?? ""}
                  />
                  {
                     showCheckBox && (<div className="absolute top-1 left-1">
                        <Checkbox
                           checked={isSelected}
                           onClick={(e) => e.stopPropagation()}
                           onChange={(e) =>
                           {
                              e.stopPropagation();
                              toggleItem(item);
                           }}
                        />
                     </div>)
                  }


               </div>

               {/* INFO */}

               <div className="flex-1 min-w-0 cart-card-info">
                  <div className="flex justify-between gap-2">
                     <div className="min-w-0 flex-1">
                        <div className="text-xs text-gray-400 truncate">
                           {item?.collection ?? item.properties?.platform}
                        </div>

                        <div className="text-sm font-medium mb-2 truncate" title={item?.id}>{item?.id}</div>
                     </div>

                     <Dropdown
                        menu={{
                           items: menuItems,
                           onClick: (e) =>
                           {
                              e.domEvent.stopPropagation();
                           },
                        }}
                        trigger={["click"]}
                     >
                        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                           <MoreOutlined className="text-gray-400" />
                        </div>
                     </Dropdown>
                  </div>

                  <div className="cart-card-metrics gap-x-6 gap-y-1 text-xs text-gray-300">
                     <div className="flex gap-2">
                        <span className="flex gap-1">
                           <CommonIcons.DatetimeIcon /> {t("Date time")}:
                        </span>
                        <span className="text-[var(--color-main-text)]">
                           {item.properties?.datetime
                              ? dayjs(item.properties?.datetime).format("DD/MM/YYYY")
                              : "---"}
                        </span>
                     </div>

                     <div className="flex gap-2">
                        <span className="flex gap-1">
                           <CommonIcons.CloudIcon /> {t("Cloud cover")}:
                        </span>
                        <span className="text-[var(--color-main-text)]">
                           {item.properties?.["eo:cloud_cover"]
                              ? item.properties?.["eo:cloud_cover"]?.toFixed(2)
                              : "---"}
                           %
                        </span>
                     </div>

                     <div className="flex gap-2">
                        <span className="flex gap-1">
                           <CommonIcons.CloudIcon /> {t("Incidence angle")}:
                        </span>
                        <span className="text-[var(--color-main-text)]">
                           {item.properties?.["view:off_nadir"]
                              ? item.properties?.["view:off_nadir"]?.toFixed(2)
                              : "---"}
                        </span>
                     </div>

                     <div className="flex gap-2">
                        <span className="flex gap-1">
                           <CommonIcons.GsdIcon /> {t("Resolution")}:
                        </span>
                        <span className="text-[var(--color-main-text)]">
                           {item.properties?.gsd
                              ? item.properties?.gsd?.toFixed(2)
                              : "---"}
                        </span>
                     </div>
                  </div>
               </div>
            </div>
         </div>
         </Spin>

         {openInfo && (
            <CommonComponents.ModalUI
               open={openInfo}
               title={t("Information")}
               toggle={() => setOpenInfo(false)}
               handleConfirm={() => { }}
               hiddenFooter={true}
               width={1120}
            >
               <Spin spinning={false}>
                  <ImageInfo data={item} open={openInfo} />
               </Spin>
            </CommonComponents.ModalUI>
         )}
      </>
   );
};

export default CardItemInCart;
